import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { collection, getDocs, doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { getCollection } from '../../services/firestoreService';

const CreateUsers = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [convenios, setConvenios] = useState([]);
  const [selectedConvenio, setSelectedConvenio] = useState('');
  const [profesionales, setProfesionales] = useState([]);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const usersFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const nombre = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return nombre.includes(q) || email.includes(q);
    });
  }, [users, busqueda]);

  useEffect(() => {
    fetchUsers();
    fetchConvenios();
    fetchProfesionales();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const data = await getCollection('branches');
      setBranches(data.filter((b) => b.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchConvenios = async () => {
    try {
      const conveniosData = await getCollection('convenios');
      setConvenios(conveniosData.filter(c => c.isActive !== false));
    } catch (error) {
      console.error('Error fetching convenios:', error);
    }
  };

  const fetchProfesionales = async () => {
    try {
      const profsData = await getCollection('professionals');
      setProfesionales(profsData.filter(p => p.active !== false));
    } catch (error) {
      console.error('Error fetching professionals:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (role === 'derivador' && !selectedConvenio) {
      setSnackbar({
        open: true,
        message: 'Selecciona un convenio para el perfil Derivador',
        severity: 'warning'
      });
      setLoading(false);
      return;
    }
    if (['Médico Veterinario', 'Director Médico', 'Radiologo'].includes(role) && !selectedProfessionalId) {
      setSnackbar({
        open: true,
        message: 'Selecciona el profesional vinculado',
        severity: 'warning'
      });
      setLoading(false);
      return;
    }

    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Guardar datos adicionales en Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        role: role,
        name: name,
        convenioId: role === 'derivador' ? selectedConvenio : '',
        convenioName: role === 'derivador'
          ? (convenios.find(c => c.id === selectedConvenio)?.name || '')
          : '',
        professionalId: ['Médico Veterinario', 'Director Médico', 'Radiologo'].includes(role) ? selectedProfessionalId : '',
        branchIds: selectedBranches.map((b) => b.id),
        createdAt: new Date()
      });

      setSnackbar({
        open: true,
        message: 'Usuario creado exitosamente',
        severity: 'success'
      });

      // Limpiar formulario
      setEmail('');
      setPassword('');
      setName('');
      setRole('user');
      setSelectedConvenio('');
      setSelectedProfessionalId('');
      setSelectedBranches([]);

      // Actualizar lista
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        setSnackbar({
          open: true,
          message: 'Usuario eliminado exitosamente',
          severity: 'success'
        });
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        setSnackbar({
          open: true,
          message: 'Error al eliminar usuario',
          severity: 'error'
        });
      }
    }
  };

  const handleOpenEditDialog = (user) => {
    setEditDialog({ open: true, user: { ...user } });
  };

  const handleCloseEditDialog = () => {
    setEditDialog({ open: false, user: null });
  };

  const handleUpdateUser = async () => {
    try {
      const { id, ...updateData } = editDialog.user;
      await updateDoc(doc(db, 'users', id), updateData);
      setSnackbar({
        open: true,
        message: 'Usuario actualizado exitosamente',
        severity: 'success'
      });
      handleCloseEditDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      setSnackbar({
        open: true,
        message: 'Error al actualizar usuario',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'Médico Veterinario':
        return 'success';
      case 'derivador':
        return 'secondary';
      case 'user':
        return 'primary';
      case 'gestion':
        return 'warning';
      case 'Radiologo':
        return 'info';
      case 'Director Médico':
        return 'secondary';
      case 'Gerente Sucursal':
        return 'primary';
      case 'Supervisor de Caja':
        return 'info';
      case 'Gestión Clínica':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Crear Usuario
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Crea nuevos usuarios y asigna roles de acceso
      </Typography>
      <Box component="form" onSubmit={handleCreateUser} sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              fullWidth
              label="Nombre Completo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              fullWidth
              label="Correo Electrónico"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              fullWidth
              label="Contraseña"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              helperText="Mínimo 6 caracteres"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              fullWidth
              select
              label="Rol"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
            >
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="Médico Veterinario">Médico Veterinario</MenuItem>
              <MenuItem value="Tecnólogo Médico">Tecnólogo Médico</MenuItem>
              <MenuItem value="Radiologo">Radiólogo</MenuItem>
              <MenuItem value="Director Médico">Director Médico</MenuItem>
              <MenuItem value="Gerente Sucursal">Gerente Sucursal</MenuItem>
              <MenuItem value="Supervisor de Caja">Supervisor de Caja</MenuItem>
              <MenuItem value="user">Usuario</MenuItem>
              <MenuItem value="gestion">Gestión</MenuItem>
              <MenuItem value="Gestión Clínica">Gestión Clínica</MenuItem>
              <MenuItem value="derivador">Derivador</MenuItem>
            </TextField>
          </Grid>
          {role === 'derivador' && (
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                select
                label="Convenio (Derivador)"
                value={selectedConvenio}
                onChange={(e) => setSelectedConvenio(e.target.value)}
                required
                helperText={
                  convenios.length === 0
                    ? 'No hay convenios configurados. Ve a Configuración → Convenios'
                    : `${convenios.length} convenios disponibles`
                }
              >
                <MenuItem value="">Seleccionar convenio...</MenuItem>
                {convenios
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((conv) => (
                    <MenuItem key={conv.id} value={conv.id}>
                      {conv.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
          )}
          {['Médico Veterinario', 'Director Médico', 'Radiologo'].includes(role) && (
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                select
                label="Profesional vinculado"
                value={selectedProfessionalId}
                onChange={(e) => setSelectedProfessionalId(e.target.value)}
                required
                helperText="Vincula este usuario con su ficha de profesional en la agenda"
              >
                <MenuItem value="">Seleccionar profesional...</MenuItem>
                {profesionales
                  .sort((a, b) => (a.apellidos || '').localeCompare(b.apellidos || ''))
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      Dr/a. {p.nombre} {p.apellidos}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
          )}
          <Grid size={12}>
            <Autocomplete
              multiple
              options={branches}
              value={selectedBranches}
              onChange={(e, value) => setSelectedBranches(value)}
              getOptionLabel={(b) => b.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sucursales asignadas"
                  helperText="El usuario solo verá datos de estas sucursales. Vacío = todas (solo recomendado para admin)."
                />
              )}
              noOptionsText="Sin sucursales configuradas"
            />
          </Grid>
          <Grid size={12}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Usuario'}
            </Button>
          </Grid>
        </Grid>
      </Box>
      {/* Lista de usuarios existentes */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            Usuarios Existentes
          </Typography>
          <TextField
            size="small"
            placeholder="Buscar por nombre o email"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Sucursales</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {busqueda.trim()
                      ? 'No hay usuarios que coincidan con la búsqueda.'
                      : 'No hay usuarios registrados.'}
                  </TableCell>
                </TableRow>
              ) : usersFiltrados.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name || 'N/A'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        user.role === 'admin'
                          ? 'Administrador'
                          : user.role === 'Médico Veterinario'
                            ? 'Médico Veterinario'
                            : user.role === 'derivador'
                              ? 'Derivador'
                              : user.role === 'gestion'
                                ? 'Gestión'
                                : user.role === 'Director Médico'
                                  ? 'Director Médico'
                                  : user.role === 'Gerente Sucursal'
                                    ? 'Gerente Sucursal'
                                    : user.role === 'Supervisor de Caja'
                                      ? 'Supervisor de Caja'
                                      : user.role === 'Gestión Clínica'
                                        ? 'Gestión Clínica'
                                        : 'Usuario'
                      }
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const ids = user.branchIds || [];
                      if (!ids.length) return <Chip label="Todas" size="small" variant="outlined" />;
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {ids.map((id) => {
                            const b = branches.find((br) => br.id === id);
                            return <Chip key={id} label={b?.name || '—'} size="small" variant="outlined" />;
                          })}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(user)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteUser(user.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Dialog de edición */}
      <Dialog open={editDialog.open} onClose={handleCloseEditDialog}>
        <DialogTitle>Editar Usuario</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nombre"
              value={editDialog.user?.name || ''}
              onChange={(e) =>
                setEditDialog({
                  ...editDialog,
                  user: { ...editDialog.user, name: e.target.value }
                })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              select
              label="Rol"
              value={editDialog.user?.role || 'user'}
              onChange={(e) =>
                setEditDialog({
                  ...editDialog,
                  user: { ...editDialog.user, role: e.target.value }
                })
              }
            >
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="Médico Veterinario">Médico Veterinario</MenuItem>
              <MenuItem value="Tecnólogo Médico">Tecnólogo Médico</MenuItem>
              <MenuItem value="Radiologo">Radiólogo</MenuItem>
              <MenuItem value="Director Médico">Director Médico</MenuItem>
              <MenuItem value="Gerente Sucursal">Gerente Sucursal</MenuItem>
              <MenuItem value="Supervisor de Caja">Supervisor de Caja</MenuItem>
              <MenuItem value="user">Usuario</MenuItem>
              <MenuItem value="gestion">Gestión</MenuItem>
              <MenuItem value="Gestión Clínica">Gestión Clínica</MenuItem>
              <MenuItem value="derivador">Derivador</MenuItem>
            </TextField>
            {['Médico Veterinario', 'Director Médico', 'Radiologo'].includes(editDialog.user?.role) && (
              <TextField
                fullWidth
                select
                label="Profesional vinculado"
                sx={{ mt: 2 }}
                value={editDialog.user?.professionalId || ''}
                onChange={(e) =>
                  setEditDialog({
                    ...editDialog,
                    user: { ...editDialog.user, professionalId: e.target.value }
                  })
                }
                helperText="Vincula este usuario con su ficha de profesional en la agenda"
              >
                <MenuItem value="">Seleccionar profesional...</MenuItem>
                {profesionales
                  .sort((a, b) => (a.apellidos || '').localeCompare(b.apellidos || ''))
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      Dr/a. {p.nombre} {p.apellidos}
                    </MenuItem>
                  ))}
              </TextField>
            )}
            {editDialog.user?.role === 'derivador' && (
              <TextField
                fullWidth
                select
                label="Convenio (Derivador)"
                sx={{ mt: 2 }}
                value={editDialog.user?.convenioId || ''}
                onChange={(e) => {
                  const convenioId = e.target.value;
                  const convenio = convenios.find(c => c.id === convenioId);
                  setEditDialog({
                    ...editDialog,
                    user: {
                      ...editDialog.user,
                      convenioId,
                      convenioName: convenio?.name || ''
                    }
                  });
                }}
                helperText={
                  convenios.length === 0
                    ? 'No hay convenios configurados. Ve a Configuración → Convenios'
                    : `${convenios.length} convenios disponibles`
                }
              >
                <MenuItem value="">Seleccionar convenio...</MenuItem>
                {convenios
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((conv) => (
                    <MenuItem key={conv.id} value={conv.id}>
                      {conv.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={branches}
              value={branches.filter((b) => (editDialog.user?.branchIds || []).includes(b.id))}
              onChange={(e, value) =>
                setEditDialog({
                  ...editDialog,
                  user: { ...editDialog.user, branchIds: value.map((b) => b.id) }
                })
              }
              getOptionLabel={(b) => b.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sucursales asignadas"
                  helperText="El usuario solo verá datos de estas sucursales. Vacío = todas."
                />
              )}
              noOptionsText="Sin sucursales configuradas"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancelar</Button>
          <Button onClick={handleUpdateUser} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateUsers;
